import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {I18n, Translate} from 'react-redux-i18n';
import Modal from 'react-modal';
import {Tooltip} from '@material-ui/core';

import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import {Icon, documentUtils, DocumentDetails, NoCurrentVersion} from 'front-onceforall-core';

import config from '../../../../config';

import {openOrSaveFile} from '../../../utils/http';
import {captureError} from '../../../utils/log';
import {interventionPropTypes} from '../../../utils/propTypes';
import {addError} from '../../../actions/snackbars';
import documentService from '../../../services/document/DocumentService';
import authenticationService from '../../../services/authentication/AuthenticationService';

import UploadPanel from '../../document/UploadPanel';
import {PREVENT_SPECIAL_DOWNLOAD_TYPES} from '../../subcontractorDetails/documents/utils';

export function ProjectTreeRowDocuments(props) {
  const [documents, setDocuments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [correlationIdOpened, setCorrelationIdOpened] = React.useState(null);

  let triggerUpload = () => { /* placeholder */ };

  const isBackOffice = authenticationService.roles().isBackOffice();
  const isOwnOrganisation = props.intervention.organisation.organisationId === props.organisation.organisationId;
  const getDocumentPermissions = doc => ({
    canEditAdditionalDesignation: false,
    canSeeSignAction: false,
    canSign: false,
    canUpload: isOwnOrganisation,
    canUploadByMandate: isOwnOrganisation && isBackOffice,
    canDownload: true,
    canDownloadExcel: !PREVENT_SPECIAL_DOWNLOAD_TYPES.includes(doc?.type?.code),
    canDownloadWithInvalidState: isOwnOrganisation || isBackOffice,
    canDownloadProof: !PREVENT_SPECIAL_DOWNLOAD_TYPES.includes(doc?.type?.code),
    canDuplicate: false,
    canDelete: false,
    isBackOffice,
    canSeePayrollRatio: true
  });

  const processDocuments = rawDocuments => {
    let documentVersions = rawDocuments.map(documentUtils.computeHasAnotherOccurrenceInSameDossier(rawDocuments));
    documentVersions = documentUtils.groupDocumentsByCorrelationId(documentVersions);
    documentVersions = documentUtils.removeArchivedCorrelationIds(documentVersions);
    return documentUtils.groupAndOrderDocumentVersionsByDossierType(documentVersions);
  };

  const getDocuments = () => {
    function getSearchDocumentPromise() {
      return props.intervention.interventionId === props.myIntervention.interventionId ?
        documentService.searchByProject(props.intervention.interventionId) :
        documentService.searchByProjectOfSubcontractor(
          props.intervention.interventionId,
          props.intervention.organisation.organisationId,
          props.organisation.organisationId
        );
    }

    setLoading(true);
    setDocuments([]);

    getSearchDocumentPromise()
      .then(response => {
        setDocuments(processDocuments(response));
        setLoading(false);
      }).catch(error => {
        captureError('Error while getting documents from a project participant', error);
        props.onCloseRequest();
      });
  };

  const handleDocumentUpload = document => {
    triggerUpload(document);
  };

  const handleDocumentFileOpen = (document, excel) => {
    const interventionId = isOwnOrganisation ? null : props.intervention.interventionId;
    authenticationService.authRedirect(documentService.getFileUrl(document.uid, excel, interventionId))
      .then(authUrl => openOrSaveFile(authUrl));
  };

  const handleDocumentProofFileOpen = document => {
    authenticationService.authRedirect(documentService.getFileProofUrl(document.uid, props.intervention.interventionId))
      .then(authUrl => openOrSaveFile(authUrl));
  };

  const documentActions = {
    onUpload: handleDocumentUpload,
    onAddNewVersion: handleDocumentUpload,
    onDownload: document => handleDocumentFileOpen(document, false),
    onDownloadExcel: document => handleDocumentFileOpen(document, true),
    onDownloadProof: handleDocumentProofFileOpen,
    onOpen: document => {
      window.location.href = `${config.backOfficeUrl}/documents/${document.uid}`;
    }
  };

  React.useEffect(getDocuments, [props]);

  const getDossierName = dossier => {
    if (dossier === 'BUSINESS') {
      return 'dossier.labels.BUSINESS.ALL';
    }

    return `dossier.labels.${dossier}`;
  };

  const renderNoCurrentVersion = hasAtDateContext => {
    // Currently projectContext applies only to FR_INS_DEC
    if (hasAtDateContext) {
      return (
        <NoCurrentVersion>
          <div className="flex-container">
            <Translate
              value={`document.documentDetails.${props.intervention.interventionDate ? 'noPresentDocumentAtInterventionDate' : 'noPresentDocumentAtDate'}`}
              projectOpeningDate={I18n.l(props.myIntervention.project.openingDate, {dateFormat: 'datetime.shortDate'})}
              interventionDate={I18n.l(props.intervention.interventionDate, {dateFormat: 'datetime.shortDate'})}
            />
            {props.intervention.interventionDate &&
              <Tooltip
                title={<Translate value="document.documentDetails.noPresentDocumentAtInterventionDateTooltip"/>}
                placement="right"
              >
                <div className="information-primary flex-container">
                  <Icon
                    icon="info"
                    size={18}
                  />
                </div>
              </Tooltip>
            }
          </div>
        </NoCurrentVersion>
      );
    }

    return (
      <NoCurrentVersion/>
    );
  };

  const renderDocuments = () => documents.map(({dossier, documentsVersions}) => (
    <React.Fragment key={dossier}>
      <h2 className="mt-5 mb-4">
        <Translate value={getDossierName(dossier)}/>
      </h2>
      {documentsVersions.map(documentVersions => <DocumentDetails
        key={dossier + documentVersions[0].correlationId}
        documentVersions={documentVersions}
        documentPermissions={getDocumentPermissions(documentVersions[0])}
        documentActions={documentActions}
        correlationIdOpened={correlationIdOpened}
        handleClick={correlationId => setCorrelationIdOpened(correlationId)}
        getTimelineVersionsFn={() => documentService.getDocumentArchivesByIntervention(
          props.intervention.interventionId,
          props.organisation.organisationId,
          documentVersions[0].correlationId
        )}
        noCurrentVersion={
          renderNoCurrentVersion(
            (documentVersions[0].projectContext?.length > 0)
          )
        }
      />)}
    </React.Fragment>
  ));

  return (
    <>
      <Modal
        defaultStyles={{
          overlay: {
            backgroundColor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 12
          },
          content: {
            backgroundColor: '#fafafa',
            borderRadius: 4,
            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 0px 32px 0px',
            minWidth: '80vw',
            outline: 0,
            padding: 20,
            position: 'relative',
            maxHeight: '90vh',
            maxWidth: '90vw',
            minHeight: 140,
            overflow: 'auto'
          }
        }}
        isOpen
        onRequestClose={props.onCloseRequest}
        ariaHideApp={false}
        shouldCloseOnOverlayClick
        shouldFocusAfterRender={false}
      >
        {loading && <span className="loading-overlay"/>}
        {!loading && <h2 className="inline-container w-100 space-between">
          <Translate
            value="project.organisationDocuments"
            organisationName={props.intervention.organisation.fullName}
          />
          <button
            type="button ml-5"
            onClick={props.onCloseRequest}
          >
            <Icon icon="cross"/>
          </button>
        </h2>}
        {!loading && isEmpty(documents) && (
          <h3 className="information mt-5">
            <Translate value="project.organisationHasNoDocuments"/>
          </h3>
        )}

        {renderDocuments()}
      </Modal>
      <UploadPanel
        organisationId={props.organisation.organisationId}
        setUploadTrigger={uploadFunction => {
          triggerUpload = uploadFunction;
        }}
        onUploadSuccess={getDocuments}
        refreshDocuments={getDocuments}
        showUploadPanelMessage={true}
        intervention={props.intervention}
        myIntervention={props.myIntervention}
      />
    </>
  );
}

ProjectTreeRowDocuments.propTypes = {
  intervention: interventionPropTypes().isRequired,
  myIntervention: interventionPropTypes().isRequired,
  organisation: PropTypes.object.isRequired,
  actions: PropTypes.object,
  onCloseRequest: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({addError}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectTreeRowDocuments);
